import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, MapPin, Bluetooth, Smartphone, ShieldCheck, Car } from 'lucide-react';
import { I270_SVG_POINTS, projectToSvg } from '../lib/i270';

const HowItWorks = () => {
  return (
    <div className="bg-cream-light" data-testid="how-it-works-page">
      {/* Hero */}
      <section className="hero-gradient py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-navy mb-4">
            How Advecti Works
          </h1>
          <p className="text-xl text-navy/70 max-w-3xl mx-auto">
            Whether you're a driver looking to earn or a business ready to advertise, 
            getting started is simple. Here's how it works.
          </p>
        </div>
      </section>

      {/* Earnings Zone Diagram */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-navy">Driver Earnings by Zone</h2>
            <p className="mt-4 text-xl text-navy/70">Earnings are based on proximity to the advertiser's target area</p>
          </div>
          
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Zone Diagram - I-270 Trace (projected from real lat/lng) */}
            <div className="flex-1 flex justify-center">
              <div className="relative w-80 h-80">
                <svg viewBox="0 0 320 320" className="w-full h-full">
                  {/* I-270 + 0.5mi buffer polygon, projected from GPS coords */}
                  <polygon
                    points={I270_SVG_POINTS(320, 320, 30)}
                    fill="rgba(251,146,60,0.2)"
                    stroke="#f97316"
                    strokeWidth="3"
                    strokeLinejoin="round"
                  />
                  {/* Cardinal labels */}
                  <text x="160" y="22" textAnchor="middle" fontSize="11" fill="#1e3a5f" fontWeight="600" opacity="0.5">N</text>
                  {/* Downtown Columbus marker */}
                  {(() => {
                    const [cx, cy] = projectToSvg(39.962, -83.000, 320, 320, 30);
                    return (
                      <>
                        <circle cx={cx} cy={cy} r="3.5" fill="#1e3a5f" />
                        <text x={cx + 6} y={cy + 4} fontSize="9" fill="#1e3a5f" fontWeight="600">Columbus</text>
                      </>
                    );
                  })()}
                  {/* Mini Zones */}
                  {(() => {
                    const zones = [
                      [40.030, -83.060, 18], // Upper Arlington
                      [39.985, -82.940, 17], // Bexley
                      [39.950, -83.005, 20], // Brewery District
                    ];
                    return zones.map(([lat, lng, r], i) => {
                      const [cx, cy] = projectToSvg(lat, lng, 320, 320, 30);
                      return (
                        <circle key={i} cx={cx} cy={cy} r={r}
                          fill="rgba(74,222,128,0.35)" stroke="#22c55e" strokeWidth="2" />
                      );
                    });
                  })()}
                  {/* Bubbles */}
                  {(() => {
                    const bubbles = [
                      [40.080, -83.050, 5],
                      [39.998, -82.995, 6],
                      [39.940, -82.870, 5],
                      [40.050, -82.910, 5],
                    ];
                    return bubbles.map(([lat, lng, r], i) => {
                      const [cx, cy] = projectToSvg(lat, lng, 320, 320, 30);
                      return (
                        <circle key={i} cx={cx} cy={cy} r={r}
                          fill="rgba(168,85,247,0.7)" stroke="#9333ea" strokeWidth="1.5" />
                      );
                    });
                  })()}
                </svg>
                {/* Labels */}
                <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-orange-400 text-white text-xs font-bold px-2 py-1 rounded">
                  I-270
                </div>
                <div className="absolute top-[30%] left-[8%] bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                  Mini Zones
                </div>
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">
                  Bubbles
                </div>
              </div>
            </div>
            
            {/* Zone Legend */}
            <div className="flex-1 space-y-4">
              <p className="font-bold text-navy text-lg mb-2">Earnings by Zone:</p>
              
              {/* Full Wrap Header */}
              <p className="font-semibold text-navy text-sm uppercase tracking-wide">Full Wrap</p>
              
              {/* Mini Zones */}
              <div className="bg-cream p-4 rounded-xl border border-navy/10">
                <p className="font-bold text-navy">Premium Mini Zones</p>
                <p className="text-green-600 font-bold mt-1">$8.00/hr <span className="text-navy/50 font-normal text-sm">(Tier 3 full rate)</span></p>
                <p className="text-navy/70 text-sm mt-1">Advertiser-selected zones within Columbus. Adjustable size, total area up to 30 sq mi.</p>
                <p className="text-purple-600 text-sm mt-1">Idle Bubble (flat, not tiered): $1.00/hr full hours · $0.50/hr half hours</p>
              </div>
              
              {/* I-270 Zone */}
              <div className="bg-cream p-4 rounded-xl border border-navy/10">
                <p className="font-bold text-navy">I-270 Zone</p>
                <p className="text-orange-500 font-bold mt-1">$4.00/hr <span className="text-navy/50 font-normal text-sm">(Tier 3 full rate)</span></p>
                <p className="text-navy/70 text-sm mt-1">Anywhere within the I-270 loop, outside of premium mini zones.</p>
                <p className="text-purple-600 text-sm mt-1">Idle Bubble (flat, not tiered): $0.50/hr full hours · $0.25/hr half hours</p>
              </div>
              
              {/* Outside/Idle */}
              <div className="bg-cream p-4 rounded-xl border border-navy/10">
                <p className="font-bold text-navy">Outside I-270 / Idle (Outside of bubbles)</p>
                <p className="text-red-500 font-bold">$0/hour</p>
              </div>
              
              {/* Partial Wrap Header */}
              <p className="font-semibold text-navy text-sm uppercase tracking-wide mt-4">Partial Wrap</p>
              
              <div className="bg-cream p-4 rounded-xl border border-navy/10">
                <p className="font-bold text-navy">Premium Mini Zones</p>
                <p className="text-green-600 font-bold mt-1">$5.00/hr <span className="text-navy/50 font-normal text-sm">(Tier 3 full rate)</span></p>
                <p className="text-purple-600 text-sm mt-1">Idle Bubble (flat, not tiered): $0.60/hr full hours · $0.30/hr half hours</p>
              </div>
              
              <div className="bg-cream p-4 rounded-xl border border-navy/10">
                <p className="font-bold text-navy">I-270 Zone</p>
                <p className="text-orange-500 font-bold mt-1">$2.50/hr <span className="text-navy/50 font-normal text-sm">(Tier 3 full rate)</span></p>
                <p className="text-purple-600 text-sm mt-1">Idle Bubble (flat, not tiered): $0.30/hr full hours · $0.15/hr half hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* High Foot Traffic Bubbles Section */}
      <section className="py-16 bg-cream">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-navy">High Foot Traffic Bubbles</h2>
            <p className="mt-4 text-xl text-navy/70">Earn money even while parked</p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-navy/10 rounded-full flex items-center justify-center">
                  <MapPin className="text-navy" size={48} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-navy mb-3">What are Bubbles?</h3>
                <p className="text-navy/70 mb-4">
                  Bubbles are designated high foot traffic areas around Columbus — shopping centers, event venues, busy downtown streets, and popular gathering spots. When you park your wrapped car in one of these bubbles, you keep earning a flat idle rate because your advertisement is still getting seen by hundreds of people passing by.
                </p>
                <p className="text-navy/70 mb-4">
                  This means you don't always have to be driving to earn. Stop for coffee, run an errand, or grab lunch in a bubble zone and your car keeps working for you. <strong>Idle-bubble pay is flat — it does not scale with your monthly tier.</strong>
                </p>
                <div className="bg-cream/50 rounded-xl p-4 mt-4">
                  <p className="font-bold text-navy mb-2">Idle Bubble Rates (Full Hours / Half Hours):</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="font-semibold text-navy">Full Wrap</p>
                      <p className="text-navy/70">Premium Zones: $1.00/hr / $0.50/hr</p>
                      <p className="text-navy/70">I-270 Zone: $0.50/hr / $0.25/hr</p>
                    </div>
                    <div>
                      <p className="font-semibold text-navy">Partial Wrap</p>
                      <p className="text-navy/70">Premium Zones: $0.60/hr / $0.30/hr</p>
                      <p className="text-navy/70">I-270 Zone: $0.30/hr / $0.15/hr</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Driving Hours Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-navy">Driving Hours</h2>
            <p className="mt-4 text-xl text-navy/70">Earnings vary by time of day</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-100 p-6 rounded-2xl border border-gray-300 text-center">
              <h3 className="text-xl font-bold text-navy mb-2">Full Hours</h3>
              <p className="text-green-600 font-bold text-lg mb-2">8:00 AM - 10:00 PM</p>
              <p className="text-navy/70">Drivers earn the normal rate during full hours.</p>
            </div>
            <div className="bg-gray-100 p-6 rounded-2xl border border-gray-300 text-center">
              <h3 className="text-xl font-bold text-navy mb-2">Half Hours</h3>
              <p className="text-orange-500 font-bold text-lg mb-2">10:00 PM - 2:00 AM &amp; 6:00 AM - 8:00 AM</p>
              <p className="text-navy/70">Drivers earn half the normal rate during these hours.</p>
            </div>
            <div className="bg-gray-100 p-6 rounded-2xl border border-gray-300 text-center">
              <h3 className="text-xl font-bold text-navy mb-2">Dark Hours</h3>
              <p className="text-red-500 font-bold text-lg mb-2">2:00 AM - 6:00 AM</p>
              <p className="text-navy/70">Drivers cannot earn during dark hours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bluetooth Beacon */}
      <section className="py-16 bg-white" data-testid="bluetooth-beacon-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-navy rounded-full mb-4">
              <Bluetooth className="text-cream" size={26} />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-navy">Bluetooth Beacon Verification</h2>
            <p className="mt-4 text-xl text-navy/70 max-w-3xl mx-auto">
              The way we make sure the wrapped car is actually the car on the road.
            </p>
          </div>

          {/* The "why" */}
          <div className="bg-cream rounded-2xl p-8 mb-10 border border-navy/10">
            <h3 className="text-xl font-bold text-navy mb-3">Why it matters</h3>
            <p className="text-navy/80 leading-relaxed">
              GPS alone can&apos;t prove you&apos;re actually inside the wrapped vehicle &mdash; your phone could be in a
              friend&apos;s car, on a bus, or sitting in a coffee shop window. The beacon closes that loop. It
              is the single source of truth that links a driving session to the wrapped car our advertisers
              are paying for.
            </p>
          </div>

          {/* How it works — 3 steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-cream-light p-6 rounded-2xl border border-navy/10">
              <div className="w-10 h-10 bg-navy text-cream rounded-full flex items-center justify-center font-bold mb-3">1</div>
              <Car className="text-navy mb-2" size={22} />
              <h4 className="font-bold text-navy mb-2">Installed during wrap day</h4>
              <p className="text-sm text-navy/70 leading-relaxed">
                When your car is wrapped at our partnered shop, a small low-energy Bluetooth beacon is
                discreetly placed inside the vehicle. It runs on a multi-year coin cell battery, no wiring,
                no maintenance.
              </p>
            </div>
            <div className="bg-cream-light p-6 rounded-2xl border border-navy/10">
              <div className="w-10 h-10 bg-navy text-cream rounded-full flex items-center justify-center font-bold mb-3">2</div>
              <Smartphone className="text-navy mb-2" size={22} />
              <h4 className="font-bold text-navy mb-2">Your phone listens for it</h4>
              <p className="text-sm text-navy/70 leading-relaxed">
                The Advecti driver app scans for your unique beacon ID in the background. When you&apos;re
                inside the car, the signal is strong &mdash; that&apos;s the green light to start earning.
              </p>
            </div>
            <div className="bg-cream-light p-6 rounded-2xl border border-navy/10">
              <div className="w-10 h-10 bg-navy text-cream rounded-full flex items-center justify-center font-bold mb-3">3</div>
              <ShieldCheck className="text-navy mb-2" size={22} />
              <h4 className="font-bold text-navy mb-2">Earnings are gated</h4>
              <p className="text-sm text-navy/70 leading-relaxed">
                If the beacon drops out of range &mdash; you stepped out, the phone moved to another car,
                the wrap car is parked while you Uber in a different vehicle &mdash; the clock pauses
                automatically. No beacon, no pay.
              </p>
            </div>
          </div>

          {/* What it does + does NOT */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
              <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                <CheckCircle size={18} /> What the beacon does
              </h4>
              <ul className="space-y-2 text-sm text-navy/80 list-disc list-inside">
                <li>Proves the phone is physically inside the wrapped car</li>
                <li>Prevents accidental earnings when you&apos;re a passenger in another car</li>
                <li>Gives advertisers verified, audit-grade session data</li>
                <li>Pauses driving hours the moment range is lost</li>
              </ul>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                <ShieldCheck size={18} /> What the beacon does NOT do
              </h4>
              <ul className="space-y-2 text-sm text-navy/80 list-disc list-inside">
                <li>It does not track you when the app is closed</li>
                <li>It does not collect audio, photos, or contacts</li>
                <li>It does not communicate with the cloud &mdash; only with your phone</li>
                <li>It does not drain your phone battery (BLE uses &lt;1% per shift)</li>
              </ul>
            </div>
          </div>

          {/* Tamper / fraud protection */}
          <div className="bg-navy text-cream rounded-2xl p-8">
            <h3 className="text-xl font-bold mb-3">Tamper Protection</h3>
            <p className="leading-relaxed mb-3">
              The beacon is paired one-to-one with your driver profile. Moving it to another car, lending
              it to someone else, or attempting to spoof the signal will flag the session in our admin
              dashboard and pause earnings until the issue is resolved.
            </p>
            <p className="leading-relaxed text-cream/70 text-sm">
              This protects both you and the advertiser: drivers playing by the rules get paid promptly,
              and advertisers know every impression came from a real wrapped car on the road.
            </p>
          </div>
        </div>
      </section>

      {/* Incremental Pay */}
      <section className="py-16 bg-cream" data-testid="incremental-pay-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-navy">How Our Pay System Works</h2>
            <p className="mt-4 text-xl text-navy/70">
              The more you drive each month, the higher your hourly rate becomes.
            </p>
          </div>

          <p className="text-navy/80 mb-8 max-w-3xl mx-auto">
            We use a tiered system that rewards consistency. <strong>Tiers apply to driving hours only</strong> — idle-bubble pay is flat all month. Casual drivers don&apos;t earn casual money &mdash; they earn far less than the math implies.
          </p>

          {/* Tier cards — 4 tiers, 30 / 30 / 30 / 10 hours = 100-hr target */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12">
            <div className="bg-white p-5 rounded-2xl border-2 border-navy/10 text-center" data-testid="tier-1-card">
              <p className="text-xs uppercase tracking-wider text-navy/50 font-semibold">Tier 1</p>
              <h3 className="text-xl font-bold text-navy mt-1">First 30 hours</h3>
              <p className="text-3xl font-bold text-amber-600 my-3">25% rate</p>
              <p className="text-navy/70 text-xs">You earn at 25% of your base rate.</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border-2 border-navy/10 text-center" data-testid="tier-2-card">
              <p className="text-xs uppercase tracking-wider text-navy/50 font-semibold">Tier 2</p>
              <h3 className="text-xl font-bold text-navy mt-1">Hours 31&ndash;60</h3>
              <p className="text-3xl font-bold text-orange-600 my-3">50% rate</p>
              <p className="text-navy/70 text-xs">You earn at 50% of your base rate.</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border-2 border-green-300 text-center shadow-md" data-testid="tier-3-card">
              <p className="text-xs uppercase tracking-wider text-green-700 font-semibold">Tier 3 &mdash; Full rate</p>
              <h3 className="text-xl font-bold text-navy mt-1">Hours 61&ndash;90</h3>
              <p className="text-3xl font-bold text-green-600 my-3">100% rate</p>
              <p className="text-navy/70 text-xs">You earn your full rate on every hour.</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-yellow-100 p-5 rounded-2xl border-2 border-yellow-400 text-center shadow-md" data-testid="tier-4-card">
              <p className="text-xs uppercase tracking-wider text-yellow-700 font-semibold">Tier 4 &mdash; Golden rate</p>
              <h3 className="text-xl font-bold text-navy mt-1">Last 10 hours (91&ndash;100)</h3>
              <p className="text-3xl font-bold text-yellow-600 my-3">200% rate</p>
              <p className="text-navy/70 text-xs">Double pay on the final 10 hours of the month.</p>
            </div>
          </div>

          {/* The Math */}
          <div className="bg-white p-8 rounded-2xl border border-navy/10 mb-8">
            <h3 className="text-2xl font-bold text-navy mb-4">The Math Is Brutally Honest</h3>
            <p className="text-navy/80 mb-4">
              If you only drive <strong>half</strong> your monthly hours, you don&apos;t earn half the money &mdash;
              <strong> you earn far less.</strong>
            </p>
            <p className="text-navy/80 mb-6">
              A driver completing just 60 hours picks up Tier 1 + Tier 2 pay, but misses the entire Tier 3 (full rate) and Tier 4 (double pay) windows. The drivers who push through all 100 monthly hours pocket the <strong>200% Tier 4 bonus</strong> on the last 10 hours — that&apos;s where the big money is.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4">
                <p className="font-bold text-yellow-700">Drive all 100 hours</p>
                <p className="text-sm text-navy/70 mt-1">You collect Tiers 1–3 <strong>and</strong> the full <strong>200% Tier 4 bonus</strong> on hours 91–100.</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="font-bold text-green-700">Drive 90 hours</p>
                <p className="text-sm text-navy/70 mt-1">You&apos;re earning full rate but leaving the Tier 4 double-pay bonus on the table.</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="font-bold text-red-700">Drive 60 hours</p>
                <p className="text-sm text-navy/70 mt-1">You&apos;re stuck at 25%–50% rate. You&apos;re earning a fraction of your potential.</p>
              </div>
            </div>
          </div>

          {/* Commitment */}
          <div className="bg-navy text-cream p-8 rounded-2xl">
            <h3 className="text-2xl font-bold mb-3">Our Commitment to You</h3>
            <p className="mb-4 leading-relaxed">
              We&apos;re transparent about this because we want drivers who are serious. If you can commit to
              100 hours of driving per month &mdash; roughly 3.5 hours a day &mdash; you&apos;ll earn real money,
              and the Tier 4 double-pay bonus is there to reward the last 10 hours of that push. If you can&apos;t, this program
              probably isn&apos;t for you, and we&apos;d rather you know that upfront.
            </p>
            <p className="leading-relaxed">
              The drivers who succeed with Advecti make it part of their daily routine: rideshare, deliveries.
              Stack your miles, hit your hours, and watch your rate climb.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-cream mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-cream mb-8">Fill out our form and we'll help you get set up today.</p>
          <Link
            to="/get-started"
            className="inline-flex items-center gap-3 bg-cream text-navy px-10 py-5 rounded-full text-xl font-bold hover:bg-cream-light transition-all pulse-cta"
          >
            Get Started Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
